import { getUserStoriesQueryOptions } from '@/utils/fetchers/story'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import StorySequence from './StorySequence'
import StoryViewerFallback from './StoryViewerFallback'

export interface PersonalStoriesProps {
	active: boolean
	canPreviousPerson: boolean
	canNextPerson: boolean
	username: string
	rememberedStories: Map<string, string>
	playbackTimes: Map<string, number>
	readStories: Set<string>
	isMuted: boolean
	onMutedChange: (muted: boolean) => void
	onClose: () => void
	onPersonChange: (direction: 'PREVIOUS' | 'NEXT') => void
}

export default function PersonalStories({
	active,
	canPreviousPerson,
	canNextPerson,
	username,
	rememberedStories,
	playbackTimes,
	readStories,
	isMuted,
	onMutedChange,
	onClose,
	onPersonChange,
}: PersonalStoriesProps) {
	const storiesQuery = useInfiniteQuery(getUserStoriesQueryOptions(username))
	const stories = storiesQuery.data?.pages.flatMap((page) => page.data) ?? []

	const handleLoadMore = async () => {
		if (!storiesQuery.hasNextPage || storiesQuery.isFetchingNextPage) return false

		const previousLength = stories.length
		const result = await storiesQuery.fetchNextPage().catch(() => undefined)
		return (result?.data.pages.flatMap((page) => page.data).length ?? 0) > previousLength
	}

	useEffect(() => {
		if (
			storiesQuery.isSuccess &&
			stories.length === 0 &&
			storiesQuery.hasNextPage &&
			!storiesQuery.isFetchingNextPage
		) {
			void storiesQuery.fetchNextPage()
		}
	}, [stories.length, storiesQuery])

	if (stories.length === 0 && !active) return null
	if (stories.length === 0) {
		return (
			<StoryViewerFallback
				status={storiesQuery.isError ? 'error' : storiesQuery.isPending ? 'loading' : 'empty'}
				onClose={onClose}
				onRetry={storiesQuery.isError ? () => void storiesQuery.refetch() : undefined}
			/>
		)
	}

	return (
		<StorySequence
			active={active}
			stories={stories}
			initialStoryId={rememberedStories.get(username)}
			hasNextPage={Boolean(storiesQuery.hasNextPage)}
			personNavigation={{
				canPrevious: canPreviousPerson,
				canNext: canNextPerson,
				onChange: onPersonChange,
			}}
			playbackTimes={playbackTimes}
			readStories={readStories}
			isMuted={isMuted}
			onMutedChange={onMutedChange}
			onClose={onClose}
			onStoryChange={(story) => rememberedStories.set(username, story.id)}
			onLoadMore={handleLoadMore}
		/>
	)
}
